import CollectorCard from './CollectorCard';
import { sourceCard } from './sourceCards';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../services/ApiService';
import type { PassportSubjectPreview } from '../../services/api/passport';
import { defaultCardDesign, type PassportCard as Card } from '../../services/api/passportCards';
import { PassportProgramShelf } from './PassportPrograms';
type Place = PassportSubjectPreview & {
  slug?: string;
  timezone: string;
  related: Array<{ relation: string; subject: PassportSubjectPreview }>;
};
export default function PassportPlaces() {
  const { id } = useParams();
  const [items, setItems] = useState<Place[]>([]),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(true),
    [city, setCity] = useState(''),
    [offset, setOffset] = useState(0),
    [next, setNext] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void api
      .makeRequest<Place | { items: Place[]; next_offset: number | null }>(
        `/places.php?${new URLSearchParams({ id: id || '', city, offset: String(offset) })}`
      )
      .then((r) => {
        if (!r.success || !r.data) throw new Error(r.error || 'Places could not be loaded');
        if (active) {
          if ('items' in r.data) {
            setItems(r.data.items);
            setNext(r.data.next_offset);
          } else setItems([r.data]);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, city, offset]);
  const place = items[0];
  const card: Card | undefined = place
    ? {
        id: null,
        title: place.title,
        subject_type: 'place',
        subject_id: place.subject_id,
        issuer: String(place.facts?.operator || 'Bilt:Spec'),
        status: 'preview',
        design: { ...defaultCardDesign, template: 'place' },
        rules: { cap: null, evidence: 'qr' },
        subject: place,
      }
    : undefined;
  return (
    <main className="pc-page pe-places">
      <Link className="pc-link" to="/passport">
        Passport
      </Link>
      <header className="pe-program-heading">
        <span className="pe-kicker">Places to return to</span>
        <h1>{id ? place?.title || 'Place' : 'Make a day of it.'}</h1>
        <p>
          Cafés, museums, venues and destinations. Each place keeps one collectible across its
          visits and programs.
        </p>
      </header>
      {loading ? (
        <p role="status">Loading places…</p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : id && card ? (
        <div className="pe-admin-split">
          <div>
            <CollectorCard card={card} />
          </div>
          <section>
            <h2>Plan your visit</h2>
            <dl>
              {Object.entries(place.facts || {}).map(([k, v]) => (
                <div key={k}>
                  <dt>{k.replaceAll('_', ' ')}</dt>
                  <dd>{String(v)}</dd>
                </div>
              ))}
            </dl>
            <p>
              Times follow {place.timezone}. Check the program’s current earning requirements before
              visiting.
            </p>
            {place.related.length > 0 && (
              <>
                <h2>At this place</h2>
                {place.related.map((r, i) => (
                  <p key={i}>
                    {r.relation.replaceAll('_', ' ')} · {r.subject.title}
                  </p>
                ))}
              </>
            )}
            <PassportProgramShelf compact />
          </section>
        </div>
      ) : (
        <>
          <label>
            City
            <input
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          <div className="pe-program-grid">
            {items.map((p) => (
              <CollectorCard key={p.subject_id} href={`/places/${p.slug}`} card={sourceCard({kind: 'place', id: p.subject_id, title: p.title, subtitle: p.subtitle, image: p.image_url, facts: p.facts, slug: p.slug})} />
            ))}
          </div>
          {!items.length && <p>No public places match this area yet.</p>}
          {offset > 0 && (
            <button className="pc-button" onClick={() => setOffset(Math.max(0, offset - 24))}>
              Previous
            </button>
          )}
          {next !== null && (
            <button className="pc-button" onClick={() => setOffset(next)}>
              Next places
            </button>
          )}
        </>
      )}
    </main>
  );
}
